/*
 * Keep the displayed price honest.
 *
 * The section renders variant.price server side, so the figure on the page is
 * whichever variant Liquid resolved at request time. Change the pack select and
 * the number stayed put: a 3 bottle pack read $9.00 while the form correctly
 * added $25.00 of stock to the cart. The cart was right and the page was lying,
 * which is the worse half of that pair to get wrong.
 *
 * Two controls move the price and both are handled here:
 *   - the pack select, which is a plain [name="id"] whose value IS the variant
 *   - the purchase option radios, because a subscription is 15% off and the
 *     one time figure is not what a subscriber pays
 *
 * Prices arrive already run through the money filter, as a JSON map printed by
 * the section. Formatting a currency in JS means reimplementing the shop's
 * money_format and getting it wrong the first time someone sells outside USD.
 * Liquid already knows the answer, so it does the formatting and this only
 * swaps text.
 *
 * With JS off nothing here runs and the page is what it always was: the price
 * of the default variant, and a form that still submits the right one.
 */
(function () {
  'use strict';

  document.querySelectorAll('[data-peanut-product]').forEach(function (section) {
    var source = section.querySelector('[data-peanut-variant-prices]');
    var form = section.querySelector('#peanut-product-form');
    var targets = section.querySelectorAll('[data-peanut-price]');
    var compareTargets = section.querySelectorAll('[data-peanut-price-was]');
    var planPriceTargets = section.querySelectorAll('[data-peanut-plan-price]');
    var packTargets = section.querySelectorAll('[data-peanut-pack]');
    var eachTargets = section.querySelectorAll('[data-peanut-each]');
    var eachWraps = section.querySelectorAll('[data-peanut-each-wrap]');
    var cadenceTargets = section.querySelectorAll('[data-peanut-cadence]');

    if (!source || !form || !targets.length) return;

    var prices;
    try {
      prices = JSON.parse(source.textContent);
    } catch (e) {
      // A malformed map means the page keeps the server rendered price. Wrong
      // for other packs, but no worse than before this file existed.
      return;
    }

    /*
     * One selector for every shape the picker takes, and it has taken three:
     * a hidden input on a single variant product, a select while the packs were
     * a dropdown, and radio cards now.
     *
     * The radios are why this is a list rather than a single element.
     * querySelector returns the FIRST match, and for a radio group that is the
     * first pack rather than the chosen one, so reading .value off it would have
     * reported the same price whichever card was clicked. Read the checked one.
     */
    var idFields = form.querySelectorAll('[name="id"]');
    /*
     * The purchase options, radios or the one switch, are found by attribute,
     * not by name. The section
     * renames them on load and submits the plan through one hidden field
     * instead, see the inline script there for why. The name still matches when
     * that script has not run, so both are accepted.
     */
    var planFields = form.querySelectorAll('[data-peanut-plan], [type="radio"][name="selling_plan"]');
    var planField = form.querySelector('[data-peanut-plan-field]');

    if (!idFields.length) return;

    function selectedId() {
      for (var i = 0; i < idFields.length; i++) {
        // a hidden input or a select is never "checked", so fall through to it
        if (idFields[i].type !== 'radio') return idFields[i].value;
        if (idFields[i].checked) return idFields[i].value;
      }
      return null;
    }

    function selectedPlanField() {
      for (var i = 0; i < planFields.length; i++) {
        if (planFields[i].checked) return planFields[i];
      }
      return null;
    }

    function selectedPlan() {
      var field = selectedPlanField();
      return field ? field.value : '';
    }

    /*
     * A figure that changes says so. Four of them swap at once when a pack or
     * the plan changes, and in one frame that read as a flicker, or was missed
     * altogether: the headline price is 200px from the finger. A short fade up
     * from a blur turns the swap into one visible change. Only when the text
     * really differs, and never on the first pass, which would animate the page
     * load. Reduced motion keeps the fade and drops the blur.
     */
    var settled = false;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    function write(node, text) {
      if (node.textContent === text) return;
      node.textContent = text;
      if (!settled || !node.animate) return;
      var from = reduce.matches ? { opacity: 0.4 } : { opacity: 0.4, filter: 'blur(2px)' };
      var to = reduce.matches ? { opacity: 1 } : { opacity: 1, filter: 'blur(0)' };
      node.animate([from, to], { duration: 180, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' });
    }

    /*
     * Keep Shopify's recurring purchase consent in step with the choice. It is
     * the paragraph under the payment buttons that authorises repeat charges,
     * and it stayed up after One time was chosen.
     *
     * Two layers, because the script that owns it is not ours and cannot be
     * read from here. The hidden field is what it watches, so that is updated
     * here and a change is announced on it. The other layer is a :has() rule in
     * peanut.css that hides the consent while One time is checked, which holds
     * even if their script never reacts or the node arrives late.
     */
    function syncPlan(plan) {
      if (planField && planField.value !== plan) {
        planField.value = plan;
        planField.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    function update() {
      var id = selectedId();
      if (id === null) return;
      var entry = prices[id];
      if (!entry) return;

      /*
       * Plan price first, one time price as the fallback. The fallback is not
       * only for the One time radio: it also covers a plan that exists on one
       * pack and not another, which would otherwise print undefined. Today every
       * pack carries the same plan, so that branch is insurance rather than a
       * live path.
       */
      var plan = selectedPlan();
      var text = (plan && entry.plans && entry.plans[plan]) || entry.price;

      syncPlan(plan);

      /*
       * What the figure buys: the count and one bottle's size, what a bottle
       * works out at, and how often it is charged. The per bottle part is null
       * on a single bottle, where it would only repeat the headline.
       */
      var each = (plan && entry.plans_each && entry.plans_each[plan]) || entry.each;
      var chosen = selectedPlanField();
      var cadence = chosen && chosen.getAttribute('data-peanut-cadence-text');
      /*
       * The switch has no option for one time, it is just off, so nothing is
       * checked and there is no field to read the wording from. It carries the
       * off wording itself.
       */
      if (!chosen && planFields.length) {
        cadence = planFields[0].getAttribute('data-peanut-cadence-off');
      }

      packTargets.forEach(function (node) {
        if (entry.pack) write(node, entry.pack);
      });
      eachTargets.forEach(function (node) {
        if (each) write(node, each);
      });
      eachWraps.forEach(function (node) {
        node.hidden = !each;
      });
      cadenceTargets.forEach(function (node) {
        if (cadence) write(node, cadence);
      });

      targets.forEach(function (node) {
        write(node, text);
      });

      /*
       * The struck one time price, shown only while a plan is selected. Hidden
       * rather than emptied, so it takes no space and is not announced: an empty
       * <s> still occupies a gap in the flex row and still reads as an element.
       */
      compareTargets.forEach(function (node) {
        if (plan && entry.plans && entry.plans[plan]) {
          write(node, entry.price);
          node.hidden = false;
        } else {
          node.hidden = true;
        }
      });

      /*
       * Each purchase option shows what it costs for the pack now chosen, so
       * both figures are visible at once and the subscription reads as a saving
       * rather than as a number to work out. Keyed on the plan id, with the empty
       * key meaning one time.
       */
      planPriceTargets.forEach(function (node) {
        var key = node.getAttribute('data-peanut-plan-price');
        var value = key ? (entry.plans && entry.plans[key]) : entry.price;
        if (value) write(node, value);
      });

      settled = true;

      /*
       * Keep the address bar in step so a reload or a shared link lands on the
       * pack that is on screen. replaceState rather than pushState: choosing a
       * pack is not a navigation, and stacking history entries would turn the
       * back button into an undo for a dropdown.
       */
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    idFields.forEach(function (field) {
      field.addEventListener('change', update);
    });
    planFields.forEach(function (field) {
      field.addEventListener('change', update);
    });

    /*
     * Run once on load. Browsers restore a previously chosen select value on a
     * back or a soft reload, and that restore happens without firing change, so
     * without this the price and the select can disagree the moment the page
     * comes back from history.
     */
    update();

    /*
     * And again on pageshow. A page restored from the back forward cache keeps
     * its DOM and fires neither load nor change, and the consent node can also
     * arrive after the first pass because the payment button is script rendered.
     */
    window.addEventListener('pageshow', update);
  });
})();
